/**
 * useScreenWakeLock — keep the display awake while a page is mounted.
 *
 * Used by fullscreen dashboards (roster, live map, DAR, tardies…) that
 * get pinned to an ops-center TV. Without a wake lock, the display
 * dims / sleeps after a few minutes and the ops team loses visibility
 * until someone jiggles a mouse.
 *
 * Uses the Screen Wake Lock API (`navigator.wakeLock`). Graceful fallback
 * on unsupported browsers (older Safari, Firefox <126) — the hook just
 * silently no-ops and returns `{ active: false, supported: false }`.
 *
 * The browser auto-releases the lock when the tab is hidden or the
 * window loses focus (spec behaviour, not a bug). We watch
 * `visibilitychange` and re-request on `visible` so a returning tab
 * gets its lock back without a manual page reload.
 */
import { useEffect, useState } from "react";

export function useScreenWakeLock(enabled = true) {
  const supported = typeof navigator !== "undefined" && "wakeLock" in navigator;
  const [active, setActive] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!enabled || !supported) return undefined;

    let sentinel = null;
    let cancelled = false;

    const acquire = async () => {
      try {
        sentinel = await navigator.wakeLock.request("screen");
        if (cancelled) {
          sentinel.release().catch(() => {});
          return;
        }
        setActive(true);
        setError(null);
        sentinel.addEventListener("release", () => {
          if (!cancelled) setActive(false);
        });
      } catch (e) {
        // Common causes: user permission denied, low battery on some
        // browsers, or the tab isn't visible. Not fatal — dashboard
        // still renders, screen just isn't guaranteed to stay on.
        setError(e?.message || "Wake lock unavailable");
        setActive(false);
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === "visible" && sentinel === null) {
        // Browser released our lock while backgrounded — reacquire.
        acquire();
      }
    };

    acquire();
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisibility);
      if (sentinel) sentinel.release().catch(() => {});
      sentinel = null;
      setActive(false);
    };
  }, [enabled, supported]);

  return { active, supported, error };
}
